import { useRequiredContext } from "@redotech/react-util/context";
import { useTranslation } from "@redotech/redo-i18n";
import BarChart01Icon from "@redotech/redo-icons/bar-chart-01.svg";
import BarChart01FilledIcon from "@redotech/redo-icons/bar-chart-01_filled.svg";
import BookOpen01Icon from "@redotech/redo-icons/book-open-01.svg";
import BookOpen01FilledIcon from "@redotech/redo-icons/book-open-01_filled.svg";
import CalendarIcon from "@redotech/redo-icons/calendar.svg";
import CalendarFilledIcon from "@redotech/redo-icons/calendar_filled.svg";
import CallBellIcon from "@redotech/redo-icons/call-bell.svg";
import CallBellFilledIcon from "@redotech/redo-icons/call-bell_filled.svg";
import DashboardIcon from "@redotech/redo-icons/dashboard.svg";
import DashboardFilledIcon from "@redotech/redo-icons/dashboard_filled.svg";
import MessageChatCircleIcon from "@redotech/redo-icons/message-chat-circle.svg";
import MessageChatCircleFilledIcon from "@redotech/redo-icons/message-chat-circle_filled.svg";
import PlayCircleIcon from "@redotech/redo-icons/play-circle.svg";
import PlayCircleFilledIcon from "@redotech/redo-icons/play-circle_filled.svg";
import ZapIcon from "@redotech/redo-icons/zap.svg";
import ZapFilledIcon from "@redotech/redo-icons/zap_filled.svg";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import {
  PlaybookPage,
  playbookPagePath,
} from "@redotech/redo-merchant-marketing-app/common/shopper-agent/playbook-routes";
import { usePublishedShopperAgentSettings } from "@redotech/redo-merchant-marketing-app/common/shopper-agent/use-published-shopper-agent-settings";
import { LinkPrimaryTab } from "@redotech/redo-web/arbiter-components/navigation/primary/redo-primary-nav";
import { PageNavigationEntry } from "@redotech/redo-web/arbiter-components/navigation/redo-side-navigation";
import { SecondaryNavigationData } from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav";
import {
  RedoSecondaryNavItem,
  useNavTreeNode,
} from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav-item";
import { memo } from "react";
import { useMatch } from "react-router-dom";
import { shopperAgentEnabled } from "../../shopper-agent-enabled";

export function useShopperAgentNavigation(): PageNavigationEntry | null {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);
  const team = useRequiredContext(TeamContext);
  const { settings: publishedShopperAgent } =
    usePublishedShopperAgentSettings();
  const publishedEnabled = publishedShopperAgent?.enabled === true;

  const isEnabled = shopperAgentEnabled(team);
  if (!isEnabled) {
    return null;
  }

  const primaryTab: LinkPrimaryTab = {
    id: "shopper-agent",
    hasNotification: false,
    UnfilledIcon: CallBellIcon,
    FilledIcon: CallBellFilledIcon,
    type: "link",
    pathRoot: routes.shopperAgentHref,
    href: routes.shopperAgentHref,
    friendlyName: t("{{productName}}", {
      productName: "Commerce Agent",
      context:
        "navigation label containing the official Commerce Agent product name",
    }),
  };

  const secondaryNavigationData: SecondaryNavigationData | undefined =
    publishedEnabled
      ? {
          id: "shopper-agent",
          name: t("{{productName}}", {
            productName: "Commerce Agent",
            context:
              "navigation label containing the official Commerce Agent product name",
          }),
          NavContent: SecondaryShopperAgentNavigation,
        }
      : undefined;

  return {
    primaryTab,
    secondaryNavigationData,
    isActiveProduct: publishedEnabled,
  };
}

const SecondaryShopperAgentNavigation = memo(
  function SecondaryShopperAgentNavigation() {
    const { t } = useTranslation();
    const { id: nodeId, TreeContext } = useNavTreeNode({
      friendlyName: t("{{productName}}", {
        productName: "Commerce Agent",
        context:
          "navigation label containing the official Commerce Agent product name",
      }),
    });

    return (
      <TreeContext.Provider value={nodeId}>
        <ShopperAgentDashboardItem />
        <ShopperAgentAnalyticsItem />
        <ShopperAgentCalendarItem />
        <ShopperAgentConversationsItem />
        <ShopperAgentKnowledgeItem />
        <ShopperAgentPlaybookItem />
        <ShopperAgentSandboxItem />
      </TreeContext.Provider>
    );
  },
);

const ShopperAgentDashboardItem = memo(function ShopperAgentDashboardItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href: routes.shopperAgentHref }}
      FilledIcon={DashboardFilledIcon}
      friendlyName={t("Dashboard", {
        context: "Commerce Agent navigation label",
      })}
      id="shopper-agent-dashboard"
      UnfilledIcon={DashboardIcon}
    />
  );
});

const ShopperAgentAnalyticsItem = memo(function ShopperAgentAnalyticsItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: routes.getShopperAgentSubRoute("analytics"),
      }}
      FilledIcon={BarChart01FilledIcon}
      friendlyName={t("Analytics", {
        context: "Commerce Agent navigation label",
      })}
      id="shopper-agent-analytics"
      UnfilledIcon={BarChart01Icon}
    />
  );
});

const ShopperAgentCalendarItem = memo(function ShopperAgentCalendarItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: routes.getShopperAgentSubRoute("calendar"),
      }}
      FilledIcon={CalendarFilledIcon}
      friendlyName={t("Calendar", {
        context: "Commerce Agent navigation label",
      })}
      id="shopper-agent-calendar"
      UnfilledIcon={CalendarIcon}
    />
  );
});

const ShopperAgentConversationsItem = memo(
  function ShopperAgentConversationsItem() {
    const { t } = useTranslation();
    const routes = useRequiredContext(MerchantRoutesContext);

    return (
      <RedoSecondaryNavItem
        action={{
          type: "link",
          href: routes.getShopperAgentSubRoute("conversations"),
        }}
        FilledIcon={MessageChatCircleFilledIcon}
        friendlyName={t("Conversations", {
          context: "Commerce Agent navigation label",
        })}
        id="shopper-agent-conversations"
        UnfilledIcon={MessageChatCircleIcon}
      />
    );
  },
);

const ShopperAgentKnowledgeItem = memo(function ShopperAgentKnowledgeItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: routes.getShopperAgentSubRoute("knowledge"),
      }}
      FilledIcon={BookOpen01FilledIcon}
      friendlyName={t("Knowledge", {
        context:
          "Commerce Agent navigation label for its merchant knowledge base",
      })}
      id="shopper-agent-knowledge"
      UnfilledIcon={BookOpen01Icon}
    />
  );
});

const ShopperAgentPlaybookItem = memo(function ShopperAgentPlaybookItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);
  const playbookRoot = routes.getShopperAgentSubRoute("playbook");
  const anyChildSelected = useMatch(`${playbookRoot}/*`);

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: routes.getShopperAgentSubRoute(
          playbookPagePath(PlaybookPage.BRAND),
        ),
      }}
      anyChildSelected={!!anyChildSelected}
      FilledIcon={ZapFilledIcon}
      friendlyName={t("Playbook", {
        context:
          "Commerce Agent navigation label for its behavioral instructions and playbook",
      })}
      id="shopper-agent-playbook"
      UnfilledIcon={ZapIcon}
    >
      <PlaybookPageItem
        friendlyName={t("Brand", {
          context: "Commerce Agent navigation label",
        })}
        page={PlaybookPage.BRAND}
      />
      <PlaybookPageItem
        friendlyName={t("Capabilities", {
          context: "Commerce Agent navigation label",
        })}
        page={PlaybookPage.CAPABILITIES}
      />
      <PlaybookPageItem
        friendlyName={t("Guardrails", {
          context: "Commerce Agent navigation label",
        })}
        page={PlaybookPage.GUARDRAILS}
      />
      <PlaybookPageItem
        friendlyName={t("Channels", {
          context: "Commerce Agent navigation label",
        })}
        page={PlaybookPage.CHANNELS}
      />
    </RedoSecondaryNavItem>
  );
});

/** Matches the page's subtree, so a capability or channel detail page keeps its index selected. */
const PlaybookPageItem = memo(function PlaybookPageItem({
  page,
  friendlyName,
}: {
  page: PlaybookPage;
  friendlyName: string;
}) {
  const routes = useRequiredContext(MerchantRoutesContext);
  const href = routes.getShopperAgentSubRoute(playbookPagePath(page));
  const subtreeMatch = useMatch(`${href}/*`);

  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href }}
      friendlyName={friendlyName}
      id={`shopper-agent-playbook-${page}`}
      selected={!!subtreeMatch}
    />
  );
});

const ShopperAgentSandboxItem = memo(function ShopperAgentSandboxItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href: routes.getShopperAgentSubRoute("sandbox") }}
      FilledIcon={PlayCircleFilledIcon}
      friendlyName={t("Sandbox", {
        context:
          "Commerce Agent navigation label for its safe testing environment",
      })}
      id="shopper-agent-sandbox"
      UnfilledIcon={PlayCircleIcon}
    />
  );
});
